import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { Alert, Button, Card, Field, PasswordInput } from '../components/ui';
import { useT } from '../i18n/locale';

/**
 * Set a new password from a reset link.
 *
 * Creates no session — proving control of an inbox is not proving identity —
 * so on success it sends the user to sign in with the new password. The link
 * carries the token in the query string; without it the page only explains
 * how to get a valid one.
 */
export default function ResetPasswordScreen() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/reset-password', { token, newPassword: password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('reset.failed'));
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="m-0 text-2xl font-black text-white">DR Inbound</h1>
          <p className="mt-1 text-sm text-white/60">{t('login.tagline')}</p>
        </div>

        <Card className="p-6">
          {!token ? (
            <>
              <h2 className="m-0 text-lg font-bold text-slate-800">{t('reset.invalidTitle')}</h2>
              <div className="mt-3">
                <Alert tone="warn">{t('reset.noToken')}</Alert>
              </div>
              <div className="mt-5">
                <Link to="/forgot-password" className="no-underline">
                  <Button variant="secondary" className="w-full">
                    {t('forgot.title')}
                  </Button>
                </Link>
              </div>
            </>
          ) : done ? (
            <>
              <h2 className="m-0 text-lg font-bold text-slate-800">{t('reset.doneTitle')}</h2>
              <div className="mt-3">
                <Alert tone="success">{t('ok.passwordReset')}</Alert>
              </div>
              <div className="mt-5">
                <Link to="/login" className="no-underline">
                  <Button className="w-full">{t('reset.toLogin')}</Button>
                </Link>
              </div>
            </>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3">
              <h2 className="m-0 text-lg font-bold text-slate-800">{t('reset.title')}</h2>
              <p className="m-0 text-sm text-slate-500">{t('reset.intro')}</p>
              {error ? <Alert tone="error">{error}</Alert> : null}
              <Field label={t('common.newPassword')} hint={t('reset.passwordHint')}>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  autoComplete="new-password"
                  required
                />
              </Field>
              <Field
                label={t('admin.repeatNewPassword')}
                error={mismatch ? t('admin.mismatch') : undefined}
              >
                <PasswordInput
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>
              <Button type="submit" disabled={busy || !password || mismatch || !confirm}>
                {busy ? t('common.saving') : t('reset.submit')}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
